import { Fragment, useRef, useState } from "react";
import { css } from "@emotion/react";
import { QRCodeSVG } from "qrcode.react";
import VerificationInput from "react-verification-input";

import { AUTH_URL, getUrl } from "../../../../includes/routes";
import Button from "../../../Button";
import Form from "../../../form/Form";
import FormActions from "../../../form/FormActions";
import { useZitadel, ZitadelSession } from "../../../../contexts/ZitadelContext";
import { useNavigate } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { FAILED_AUTHENTICATION_PARAM, zitadelGraphqlErrors } from "../../../../includes/constants";
import { API_BASE_URL } from "../../../../../config/config";

type ZitadelCancelTotpRegistrationMutationResponse = {
    zitadelCancelTotpRegistration: ZitadelSession;
}

type ZitadelCancelTotpRegistrationMutationVariables = {
    sessionId: string;
    sessionToken: string;
}

type ZitadelCompleteTotpChallengeMutationResponse = {
    zitadelCompleteTotpChallenge: {
        id: string
    };
}

type ZitadelCompleteTotpChallengeMutationVariables = {
    sessionId: string;
    sessionToken: string;
    code: string;
}

const TotpChallenge = () => {
    const navigate = useNavigate();

    const errorElementRef = useRef<HTMLParagraphElement>(null);
    const [inputtedCode, setInputtedCode] = useState("");
    const [inputError, setInputError] = useState("");

    const [isTotpLoading, setIsTotpLoading] = useState(false);
    const [isCancelLoading, setIsCancelLoading] = useState(false);
    const [isCompletionNoticeVisible, setIsCompletionNoticeVisible] = useState(false);

    const {
        loginInfo,
        clearLoginInfo,
        session,
        setSession,
        clearSession,
        totpRegistration,
        clearTotpRegistration,
    } = useZitadel();

    const [zitadelCancelTotpRegistration] =
        useMutation<ZitadelCancelTotpRegistrationMutationResponse, ZitadelCancelTotpRegistrationMutationVariables>(
            ZITADEL_CANCEL_TOTP_REGISTRATION,
        );

    const [zitadelCompleteTotpChallenge] =
        useMutation<ZitadelCompleteTotpChallengeMutationResponse, ZitadelCompleteTotpChallengeMutationVariables>(
            ZITADEL_COMPLETE_TOTP_CHALLENGE,
        );

    const onSubmit = () => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to register TOTP.");

        setIsTotpLoading(true);

        zitadelCompleteTotpChallenge({
            variables: {
                sessionId: session.id,
                sessionToken: session.token,
                code: inputtedCode,
            },
            fetchPolicy: 'network-only',
        }).then(() => {
            if (!loginInfo?.isRedirectDisabled) {
                clearLoginInfo();
                clearSession();
                clearTotpRegistration();

                let url = API_BASE_URL + "/auth/login-redirect";
                if (loginInfo?.backUrl) url += "?BackURL=" + loginInfo.backUrl;
                window.location.href = url;
            } else {
                setIsCompletionNoticeVisible(true);
            }
        }).catch((e) => {
            const graphQlErrors = e.graphQLErrors;
            if (graphQlErrors) {
                if (
                    graphQlErrors.find(
                        (graphQlError: any) => graphQlError.extensions?.details?.includes(
                            zitadelGraphqlErrors.INVALID_MFA_CODE
                        )
                    )
                ) {
                    setInputError("Invalid code");
                    // Screen reader announces a field error message.
                    if (errorElementRef.current) errorElementRef.current.focus();
                    setIsTotpLoading(false);
                    return;
                }
            }
            clearLoginInfo();
            clearSession();
            clearTotpRegistration();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        });
    };

    const onCancel = () => {
        /** Should be impossible, but this helps typescript realise session is not null */
        if (!session) throw new Error("Failed to cancel TOTP registration.");

        setIsCancelLoading(true);

        zitadelCancelTotpRegistration({
            variables: {
                sessionId: session.id,
                sessionToken: session.token,
            },
            fetchPolicy: 'network-only',
        }).then(({ data }) => {
            if (!data) throw new Error("TOTP registration cancellation did not return a valid response.");

            const session = data.zitadelCancelTotpRegistration;

            setSession(session);
            clearTotpRegistration();
        }).catch(() => {
            clearLoginInfo();
            clearSession();
            clearTotpRegistration();
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
        });
    }

    const isActionsDisabled = isTotpLoading || isCancelLoading;

    /** Completion notice overrides all */
    if (isCompletionNoticeVisible) return <div>Authentication complete, you may now close this window</div>;

    if (!loginInfo || !session) return null;

    if (session.metadata.isMfaRegistrationRequired && !totpRegistration) return null;

    const isOneMfaMethodAllowed = session.metadata.isTotpAllowed !== session.metadata.isOtpEmailAllowed;
    const isOneMfaMethodForced = isOneMfaMethodAllowed && !session?.metadata.isSkipAllowed;

    return (
        <div css={rootStyle}>
            <h1>Multi-Factor Authentication</h1>
            {session.metadata.isMfaRegistrationRequired && !!totpRegistration && (
                <Fragment>
                    <p>
                        {
                            "Each time you log in, you will receive a verification code via an authenticator app, such as " +
                            "Google Authenticator."
                        }
                    </p>
                    <div css={groupingStyle}>
                        <h2>Step 1: Set up authenticator app</h2>
                        <p>This is a one-time step to connect your authenticator app to Writer’s Toolbox.</p>
                        <p>Open your authenticator app and scan the code below.</p>
                        <div css={qrCodeStyle}>
                            <QRCodeSVG aria-label="Authentication QR code" value={totpRegistration.uri} />
                        </div>
                        <p>
                            If you’re unable to scan the code, enter the secret key manually:
                            <br /><em>{totpRegistration.secret}</em>
                        </p>
                    </div>
                </Fragment>
            )}
            {!session.metadata.isMfaRegistrationRequired && (
                <div css={groupingStyle}>
                    <p>You have chosen to log in using an authenticator app.</p>
                    <p>Open your authenticator app and enter the six digit code shown.</p>
                </div>
            )}
            <Form onSubmit={onSubmit}>
                <div css={groupingStyle}>
                    {session.metadata.isMfaRegistrationRequired && (
                        <Fragment>
                            <h2>Step 2: Enter verification code</h2>
                            <p>Enter the six digit code shown in your authenticator app.</p>
                        </Fragment>
                    )}
                    <VerificationInput
                        length={6}
                        validChars="0-9"
                        placeholder={"-"}
                        removeDefaultStyles
                        container={{ className: "verification-container" }}
                        characters={{ className: "verification-characters" }}
                        character={{
                            className: "verification-character",
                            classNameInactive: "verification-character--inactive",
                            classNameSelected: "verification-character--selected"
                        }}
                        onChange={setInputtedCode}
                    />
                    <p css={errorStyle} ref={errorElementRef} tabIndex={-1} role={"alert"}>{inputError}</p>
                    <div css={formActionsStyle}>
                        <FormActions customCss={customFormActionsStyles}>
                            {session.metadata.isMfaRegistrationRequired && !isOneMfaMethodForced && (
                                <Button
                                    css={cancelButtonStyle}
                                    type="button"
                                    onClick={onCancel}
                                    disabled={isActionsDisabled}
                                    forceLoading={isCancelLoading}
                                    useOutline
                                >
                                    Choose another method
                                </Button>
                            )}
                            <Button
                                css={buttonStyle}
                                type="submit"
                                disabled={isActionsDisabled}
                                forceLoading={isTotpLoading}
                                useOutline
                            >
                                Confirm
                            </Button>
                        </FormActions>
                    </div>
                </div>
            </Form>
        </div>
    );
}

const ZITADEL_CANCEL_TOTP_REGISTRATION = gql`
    mutation zitadelCancelTotpRegistration($sessionId: String!, $sessionToken: String!) {
        zitadelCancelTotpRegistration(sessionId: $sessionId, sessionToken: $sessionToken) {
            id
            token
            metadata {
                userEmail
                isMfaRegistrationRequired
                isTotpChallengeRequired
                isOtpEmailChallengeRequired
                isTotpAllowed
                isOtpEmailAllowed
                isSkipAllowed
            }
        }
    }
`;

const ZITADEL_COMPLETE_TOTP_CHALLENGE = gql`
    mutation zitadelCompleteTotpChallenge($sessionId: String!, $sessionToken: String!, $code: String!) {
        zitadelCompleteTotpChallenge(sessionId: $sessionId, sessionToken: $sessionToken, code: $code) {
            id
        }
    }
`;

const rootStyle = (theme: EmotionTheme) => css`
    /** Heavily derived from SignIn.tsx::authContentStyle */
    margin-top: 40px;
    width: 510px;
    position: relative;
    ${theme.breakpoints.down('sm')} {
        width: 100%;
        max-width: 510px;
        margin: 40px auto 0;
    }
    ${theme.breakpoints.down('xs')} {
        padding: 0 20px;
    }

    /** Heavily derived from SignIn.tsx::leftHeaderStyle */
    h1 {
        margin: 0;
        color: var(--primary-colour);
        font-weight: ${theme.fonts.weights.bold};

        ${theme.breakpoints.up("xxl")} {
            font-size: 50px;
            line-height: 75px;
        }

        ${theme.breakpoints.between("md", "xl")} {
            font-size: 43px;
            line-height: 65px;
        }

        ${theme.breakpoints.down("sm")} {
            font-size: 30px;
            line-height: 45px;
        }
    }

    h2 {
        margin: 0;
    }

    p {
        margin:  0;
    }

    em {
        color: var(--primary-colour);
        font-weight: 400;
        font-style: normal;
    }

    --primary-colour: ${theme.colours.curiousBlue};

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2rem;
    padding-bottom: 10rem;
`;

const groupingStyle = css`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const qrCodeStyle = css`
    border: solid 2px black;
    padding: 0.5rem;
    width: 150px;
    height: 150px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 1rem;
`;

const errorStyle = (theme: EmotionTheme) => css`
    color: ${theme.colours.red[500]};
`;

/** Heavily derived from SignIn.tsx::formActionsStyle */
const formActionsStyle = (theme: EmotionTheme) => css`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    ${theme.breakpoints.up('sm')} {
        justify-content: center;
    }
`;

/** Heavily derived from SignIn.tsx::customFormActionsStyles */
const customFormActionsStyles = css`
    width: 100%;
    justify-content: center;
`;

/** Heavily derived from SignIn.tsx::buttonStyle */
const buttonStyle = (theme: EmotionTheme) => css`
    background-color: ${theme.colours.blue['400']};
    width: 400px;
    border-radius: 45px;
    height: 40px;
    padding: 0;

    ${theme.breakpoints.down('xs')} {
        width: 250px;
    }
`;
const cancelButtonStyle = (theme: EmotionTheme) => css`
    ${buttonStyle(theme)};
    background: none;
    color: ${theme.colours.blue['400']};
    border: 1px solid ${theme.colours.blue['400']};
`;

export default TotpChallenge;
